'use client';

import React, { Suspense, lazy } from 'react';
import { Box, Text } from '@chakra-ui/react';
import type { ViewState } from 'react-map-gl';
import type { NestedAutocompleteAddress } from '@habity/types';
import { MAPBOX_CONFIG } from '~/constants/mapbox';
import { FaMapMarkerAlt } from 'react-icons/fa';

// Lazy load map components
const Map = lazy(() =>
  import('react-map-gl').then((mod) => ({ default: mod.Map }))
);
const Marker = lazy(() =>
  import('react-map-gl').then((mod) => ({ default: mod.Marker }))
);

const { ACCESS_TOKEN, PERFORMANCE_SETTINGS } = MAPBOX_CONFIG;

interface MapViewProps {
  selectedAddress: NestedAutocompleteAddress;
  viewState: Partial<ViewState>;
  onMove: (e: { viewState: ViewState }) => void;
  mapRef?: React.RefObject<any>;
  height?: { base?: string; lg?: string };
  interactive?: boolean;
}

export function MapView({
  selectedAddress,
  viewState,
  onMove,
  mapRef,
  height = { base: '200px', lg: '250px' },
  interactive = false,
}: MapViewProps) {
  if (!selectedAddress?.latitude || !selectedAddress?.longitude) {
    return null;
  }

  return (
    <Box position="relative" w="100%" h={height} overflow="hidden">
      <Suspense fallback={<Box bg="gray.100" w="100%" h="100%" />}>
        <Map
          ref={mapRef}
          {...viewState}
          style={{
            pointerEvents: interactive ? 'inherit' : 'none',
            touchAction: interactive ? 'inherit' : 'none',
            zIndex: 0,
          }}
          mapStyle="mapbox://styles/mapbox/light-v10"
          onMove={onMove}
          mapboxAccessToken={ACCESS_TOKEN}
          dragRotate={false}
          touchZoomRotate={false}
          scrollZoom={false}
          dragPan={interactive}
          touchPitch={false}
          keyboard={false}
          {...PERFORMANCE_SETTINGS}
          reuseMaps
          attributionControl={false}
          logoPosition="bottom-right"
        >
          <Suspense fallback={null}>
            <Marker
              latitude={selectedAddress.latitude}
              longitude={selectedAddress.longitude}
              anchor="bottom"
              offset={[0, 0]}
              style={{
                zIndex: 2,
              }}
            >
              <Box position="relative">
                <FaMapMarkerAlt size={40} color="#be5103" />
                <Text
                  position="absolute"
                  top="100%"
                  left="50%"
                  transform="translateX(-50%)"
                  fontSize="sm"
                  fontWeight="semibold"
                  color="gray.800"
                  bg="white"
                  px={2}
                  py={1}
                  mt={2}
                  borderRadius="md"
                  boxShadow="sm"
                  whiteSpace="nowrap"
                >
                  {selectedAddress.street}
                </Text>
              </Box>
            </Marker>
          </Suspense>
        </Map>
      </Suspense>
    </Box>
  );
}
